import { useTranslation } from 'react-i18next';
import { ArrowRight, User } from 'lucide-react';
import {
  CATEGORY_CONFIG, displayConfidencePercent, type TalentCategory,
} from './talentMapShared';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

export interface TalentSnapshot {
  versionNumber: number;
  timestamp: string;
  category: string;
  processStatus: string;
  updatedBy: string;
  source: 'leader' | 'hrbp';
  justification?: string;
  evidenceSummary?: string;
  previousCategory?: string | null;
  agentRecommendedCategory?: string;
  confidenceLevel?: 'high' | 'medium' | 'low';
  confidencePercent?: number;
  dimensionsCovered?: string[];
  authorRole?: string;
}

function Chip({ category }: { category?: string | null }) {
  const { t } = useTranslation();
  const cfg = category ? CATEGORY_CONFIG[category as TalentCategory] : null;
  if (!cfg) return <span className="text-sm text-muted-foreground px-1">—</span>;
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap', cfg.chip)}>
      {t(cfg.labelKey)}
    </span>
  );
}

/** Vertical version timeline of the Histórico tab (spec mockup 3): numbered
 *  circles (ordinal position, newest on top) joined by a line; one card per
 *  confirmed version with badge, author, category transition, evidence and
 *  confidence/dimensions footer. Enriched fields render only when present
 *  (snapshots written before 2026-07 lack them). */
export function VersionTimeline({ snapshots }: { snapshots: TalentSnapshot[] }) {
  const { t } = useTranslation();
  const total = snapshots.length;
  const newestFirst = [...snapshots].reverse();

  return (
    <div className="space-y-4">
      {newestFirst.map((snap, idx) => {
        const ordinal = total - idx;                      // 1 = oldest
        const isCurrent = idx === 0;
        const isInitial = ordinal === 1;
        const prevCategory = snap.previousCategory !== undefined
          ? snap.previousCategory
          : (snapshots[ordinal - 2]?.category ?? null);
        const sameCategory = prevCategory != null && prevCategory === snap.category;
        const confidence = displayConfidencePercent(snap);
        const authorRole = snap.authorRole ?? (snap.source === 'hrbp' ? 'HRBP' : t('performance.talent_map.history_tab.leader_role'));
        const badge = isCurrent
          ? { label: t('performance.talent_map.history_tab.current_version'), cls: 'bg-primary text-primary-foreground' }
          : snap.source === 'hrbp'
            ? { label: t('performance.talent_map.history_tab.hrbp_updated'), cls: 'bg-muted text-muted-foreground' }
            : isInitial
              ? { label: t('performance.talent_map.history_tab.initial_evaluation'), cls: 'bg-muted text-muted-foreground' }
              : { label: t('performance.talent_map.history_tab.leader_updated'), cls: 'bg-muted text-muted-foreground' };

        return (
          <div key={`${snap.versionNumber}-${snap.timestamp}`} className="flex gap-3">
            {/* Ordinal circle + connector */}
            <div className="flex flex-col items-center shrink-0">
              <span className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold',
                isCurrent ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
              )}>
                {ordinal}
              </span>
              {idx < newestFirst.length - 1 && <span className="w-px flex-1 bg-border mt-1" />}
            </div>

            {/* Version card */}
            <div className={cn(
              'flex-1 rounded-xl border bg-card p-4 space-y-3 mb-1',
              isCurrent && 'border-foreground/60 shadow-xs',
            )}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={cn('rounded-md px-2 py-0.5 text-[11px] font-bold tracking-wide uppercase', badge.cls)}>
                    {badge.label}
                  </span>
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {format(new Date(snap.timestamp), 'dd/MM/yyyy', { locale: es })}
                  </span>
                </div>
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <User className="h-3.5 w-3.5" />
                  {snap.updatedBy.split('@')[0]} · {authorRole}
                </span>
              </div>

              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Chip category={prevCategory} />
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <Chip category={snap.category} />
                  {sameCategory && (
                    <span className="text-xs text-muted-foreground">{t('performance.talent_map.history_tab.no_change')}</span>
                  )}
                </div>
                {snap.agentRecommendedCategory && CATEGORY_CONFIG[snap.agentRecommendedCategory as TalentCategory] && (
                  <span className="text-sm text-muted-foreground">
                    {t('performance.talent_map.history_tab.agent_label')}:{' '}
                    <span className="font-semibold text-foreground/80">
                      {t(CATEGORY_CONFIG[snap.agentRecommendedCategory as TalentCategory].labelKey)}
                    </span>
                  </span>
                )}
              </div>

              {(snap.evidenceSummary || snap.justification) && (
                <div className="rounded-lg bg-muted/60 p-3">
                  <p className="text-sm text-foreground/90 leading-relaxed">
                    {snap.justification ?? snap.evidenceSummary}
                  </p>
                </div>
              )}

              {(confidence != null || (snap.dimensionsCovered?.length ?? 0) > 0) && (
                <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                  {confidence != null && (
                    <span>
                      {t('performance.talent_map.history_tab.confidence_footer')}: <span className="font-bold text-foreground">{confidence}%</span>
                    </span>
                  )}
                  {(snap.dimensionsCovered?.length ?? 0) > 0 && (
                    <span>
                      {t('performance.talent_map.history_tab.dimensions_footer')}: {snap.dimensionsCovered!.join(', ')}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
